import React, { useEffect, useRef, useState } from 'react'

const steps = [
  {
    number: "01",
    title: "Client Discovery & Requirement Analysis",
    icon: "🔍",
    points: [
      "Client discussion and requirement understanding",
      "Current challenges related to health, wealth, relationships or growth",
      "Property type identification (Home / Office / Plot / Factory)",
      "Personal goals and expectations from consultation",
    ],
  },
  {
    number: "02",
    title: "Site Access & Initial Evaluation",
    icon: "🏛️",
    points: [
      "Site access through physical visit or Zoom consultation",
      "Location and surrounding environment observation",
      "Plot shape, road direction and nearby influences",
      "Preliminary energy understanding of the space",
    ],
  },
  {
    number: "03",
    title: "Layout & Directional Mapping",
    icon: "🧭",
    points: [
      "Layout plan analysis of the property",
      "Degree-based directional mapping",
      "Division of the space into energy zones",
      "Identification of key areas like entrance, kitchen, bedroom, toilets etc.",
    ],
  },
  {
    number: "04",
    title: "Energy Audit & Detailed Analysis",
    icon: "⚡",
    points: [
      "Complete Vastu energy audit of the property",
      "Identification of imbalances and blockages",
      "Zone-wise evaluation of energies",
      "Assessment of elements affecting growth and harmony",
    ],
  },
  {
    number: "05",
    title: "Vastu Corrections & Remedies",
    icon: "✨",
    points: [
      "Practical Vastu corrections without demolition",
      "Placement based remedies and adjustments",
      "Activation of positive energy zones",
      "Remedies for removing negative influences",
    ],
  },
  {
    number: "06",
    title: "Implementation Guidance & Follow-up",
    icon: "🌿",
    points: [
      "Step-by-step implementation guidance",
      "Clarification support during corrections",
      "Follow-up discussion if required",
      "Ensuring balanced and harmonious space",
    ],
  },
]

const StepCard = ({ step, index }) => {
  const [visible, setVisible] = useState(false)
  const ref = useRef()

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true) },
      { threshold: 0.15 }
    )
    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible
          ? 'translateY(0)'
          : index % 2 === 0 ? 'translateY(40px)' : 'translateY(40px)',
        transition: `opacity 0.7s ease ${index * 0.1}s, transform 0.7s ease ${index * 0.1}s`,
        background: 'linear-gradient(135deg, rgba(130,94,10,0.08) 0%, rgba(255,255,255,0.95) 100%)',
        border: '1px solid rgba(130,94,10,0.18)',
        borderLeft: '4px solid #825e0a',
        borderRadius: '12px',
        padding: '32px 28px',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'default',
        boxShadow: '0 4px 24px rgba(130,94,10,0.07)',
        transition: `opacity 0.7s ease ${index * 0.1}s, transform 0.7s ease ${index * 0.1}s, box-shadow 0.3s ease`,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 12px 40px rgba(130,94,10,0.18)'
        e.currentTarget.style.borderLeftColor = '#c48a14'
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = '0 4px 24px rgba(130,94,10,0.07)'
        e.currentTarget.style.borderLeftColor = '#825e0a'
      }}
    >
      {/* Background number watermark */}
      <div style={{
        position: 'absolute', top: '-10px', right: '16px',
        fontSize: '90px', fontFamily: '"Playfair Display", serif',
        color: 'rgba(130,94,10,0.06)', fontWeight: '900',
        lineHeight: 1, userSelect: 'none', pointerEvents: 'none',
      }}>
        {step.number}
      </div>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '20px' }}>
        <div style={{
          width: '52px', height: '52px', borderRadius: '50%',
          background: 'linear-gradient(135deg, #825e0a, #c48a14)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '22px', flexShrink: 0,
          boxShadow: '0 4px 12px rgba(130,94,10,0.3)',
        }}>
          {step.icon}
        </div>
        <div>
          <span style={{
            fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase',
            color: '#c48a14', fontWeight: '700', fontFamily: '"Montserrat", sans-serif',
          }}>
            Step {step.number}
          </span>
          <h3 style={{
            margin: '4px 0 0', fontSize: '18px', fontWeight: '700',
            color: '#2c1a00', fontFamily: '"Playfair Display", serif',
            lineHeight: '1.3',
          }}>
            {step.title}
          </h3>
        </div>
      </div>

      {/* Points */}
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {step.points.map((point, i) => (
          <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <span style={{
              width: '18px', height: '18px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #825e0a22, #c48a1422)',
              border: '1.5px solid #825e0a55',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, marginTop: '2px',
            }}>
              <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#825e0a', display: 'block' }} />
            </span>
            <span style={{
              fontSize: '14px', color: '#5a3d10', lineHeight: '1.6',
              fontFamily: '"Lato", sans-serif',
            }}>
              {point}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

const Vastu = () => {
  const [headerVisible, setHeaderVisible] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => setHeaderVisible(true), 100)
    return () => clearTimeout(t)
  }, [])

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;900&family=Montserrat:wght@400;600;700&family=Lato:wght@300;400;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #fdf8f0; }

        .vastu-section {
          min-height: 100vh;
          background: #fdf8f0;
          font-family: 'Lato', sans-serif;
          overflow: hidden;
        }

        /* Decorative SVG mandala bg */
        .vastu-hero {
          position: relative;
          background: linear-gradient(160deg, #2c1a00 0%, #4a2e05 40%, #825e0a 100%);
          padding: 80px 24px 100px;
          text-align: center;
          overflow: hidden;
        }

        .vastu-hero::before {
          content: '';
          position: absolute;
          top: -80px; right: -80px;
          width: 360px; height: 360px;
          border-radius: 50%;
          border: 1px solid rgba(196,138,20,0.2);
          pointer-events: none;
        }
        .vastu-hero::after {
          content: '';
          position: absolute;
          bottom: -60px; left: -60px;
          width: 280px; height: 280px;
          border-radius: 50%;
          border: 1px solid rgba(196,138,20,0.15);
          pointer-events: none;
        }

        .mandala-ring {
          position: absolute;
          border-radius: 50%;
          border: 1px solid rgba(196,138,20,0.12);
          top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          pointer-events: none;
          animation: rotateSlow 60s linear infinite;
        }

        @keyframes rotateSlow {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }

        .divider-ornament {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin: 16px auto 0;
        }
        .divider-ornament .line {
          height: 1px;
          width: 60px;
          background: linear-gradient(to right, transparent, #c48a1488, #c48a14);
        }
        .divider-ornament .line.rev {
          background: linear-gradient(to left, transparent, #c48a1488, #c48a14);
        }

        .badge {
          display: inline-block;
          background: rgba(196,138,20,0.15);
          border: 1px solid rgba(196,138,20,0.4);
          color: #f0c060;
          padding: 6px 18px;
          border-radius: 40px;
          font-size: 11px;
          letter-spacing: 3px;
          text-transform: uppercase;
          font-family: 'Montserrat', sans-serif;
          font-weight: 700;
          margin-bottom: 20px;
        }

        .steps-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 24px;
          max-width: 1100px;
          margin: 0 auto;
          padding: 64px 24px 80px;
        }

        .cta-strip {
          background: linear-gradient(135deg, #825e0a, #4a2e05);
          padding: 56px 24px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        .cta-strip::before {
          content: '✦';
          position: absolute; top: 24px; left: 40px;
          color: rgba(255,255,255,0.06); font-size: 60px;
        }
        .cta-strip::after {
          content: '✦';
          position: absolute; bottom: 24px; right: 40px;
          color: rgba(255,255,255,0.06); font-size: 60px;
        }

        .cta-btn {
          display: inline-block;
          background: linear-gradient(135deg, #f0c060, #c48a14);
          color: #2c1a00;
          padding: 16px 44px;
          border-radius: 50px;
          font-family: 'Montserrat', sans-serif;
          font-weight: 700;
          font-size: 15px;
          letter-spacing: 1px;
          border: none;
          cursor: pointer;
          box-shadow: 0 8px 28px rgba(196,138,20,0.4);
          transition: transform 0.25s ease, box-shadow 0.25s ease;
          text-decoration: none;
        }
        .cta-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 14px 36px rgba(196,138,20,0.55);
        }
      `}</style>

      <section className="vastu-section">
        {/* Hero */}
        <div className="vastu-hero">
          {/* Decorative rings */}
          {[500, 700, 900].map((s, i) => (
            <div
              key={i}
              className="mandala-ring"
              style={{ width: s, height: s, animationDelay: `${i * 5}s`, animationDuration: `${50 + i * 20}s` }}
            />
          ))}

          <div style={{
            position: 'relative', zIndex: 2,
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(-30px)',
            transition: 'opacity 0.9s ease, transform 0.9s ease',
          }}>
            <div className="badge">✦ Sacred Science of Space ✦</div>

            <h1 style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 'clamp(32px, 5vw, 54px)',
              fontWeight: '900',
              color: '#fdf8f0',
              lineHeight: '1.2',
              marginBottom: '16px',
              textShadow: '0 2px 20px rgba(0,0,0,0.3)',
            }}>
              Vastu Consultation
              <br />
              <span style={{ color: '#f0c060' }}>Process</span>
            </h1>

            <div className="divider-ornament">
              <div className="line rev" />
              <span style={{ color: '#c48a14', fontSize: '18px' }}>✦</span>
              <div className="line" />
            </div>

            <p style={{
              marginTop: '20px',
              color: 'rgba(253,248,240,0.75)',
              fontSize: '17px',
              fontFamily: '"Lato", sans-serif',
              fontWeight: '300',
              letterSpacing: '0.5px',
              maxWidth: '540px',
              margin: '20px auto 0',
              lineHeight: '1.8',
            }}>
              Our Structured Approach for Accurate Vastu Analysis
            </p>
          </div>

          {/* Bottom wave */}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
            <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: '60px' }}>
              <path d="M0,60 C360,0 1080,0 1440,60 L1440,60 L0,60 Z" fill="#fdf8f0" />
            </svg>
          </div>
        </div>

        {/* Steps Grid */}
        <div className="steps-grid">
          {steps.map((step, i) => (
            <StepCard key={step.number} step={step} index={i} />
          ))}
        </div>

        {/* CTA Strip */}
        <div className="cta-strip">
          <p style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: 'clamp(22px, 3vw, 32px)',
            color: '#fdf8f0',
            marginBottom: '8px',
            fontWeight: '700',
          }}>
            Ready to Transform Your Space?
          </p>
          <p style={{
            color: 'rgba(253,248,240,0.65)',
            fontSize: '15px',
            fontFamily: '"Lato", sans-serif',
            marginBottom: '32px',
            letterSpacing: '0.5px',
          }}>
            Book your personalised Vastu consultation today
          </p>
          <a href="https://wa.me/919122397324?text=Hello%20Tarun%2C%20I%E2%80%99m%20interested%20in%20Vastu%20Consultation." className="cta-btn">
            Book a Consultation ✦
          </a>
        </div>
      </section>
    </>
  )
}

export default Vastu